import {
  LEGACY_LOCAL_STORAGE_MIGRATIONS,
  LEGACY_SESSION_STORAGE_MIGRATIONS,
} from "./storage-keys";

/**
 * Storage-like surface used by the migrator. Both `localStorage` and
 * `sessionStorage` satisfy it; tests inject in-memory stubs.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * One-shot copy of legacy `vf.*` keys onto their `castoffpro.*` counterparts.
 *
 * Rules (idempotent, safe to run on every startup):
 *   - If the new key already exists, leave it alone and just drop the legacy
 *     one. Users who already saved a preference under the new name win.
 *   - If only the legacy key exists, copy its value across, then delete it.
 *   - If neither exists, do nothing.
 *
 * The function never throws — Safari private mode and similar storage
 * lockdowns simply skip the affected scope.
 */
export function migrateVfToCastOffPro(
  local: StorageLike | null = safeStorage("local"),
  session: StorageLike | null = safeStorage("session"),
): void {
  if (local) migrateScope(local, LEGACY_LOCAL_STORAGE_MIGRATIONS);
  if (session) migrateScope(session, LEGACY_SESSION_STORAGE_MIGRATIONS);
}

function migrateScope(
  storage: StorageLike,
  pairs: ReadonlyArray<readonly [string, string]>,
): void {
  for (const [oldKey, newKey] of pairs) {
    let legacy: string | null;
    try {
      legacy = storage.getItem(oldKey);
    } catch {
      continue;
    }
    if (legacy == null) continue;

    try {
      const existing = storage.getItem(newKey);
      if (existing == null) {
        storage.setItem(newKey, legacy);
      }
      storage.removeItem(oldKey);
    } catch {
      // Quota or access errors: leave both keys in place so a retry on the
      // next reload can finish the job.
    }
  }
}

function safeStorage(scope: "local" | "session"): StorageLike | null {
  try {
    if (typeof window === "undefined") return null;
    return scope === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}
